import React, { useState } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { crmFormsApi } from '../../services/api';
import { Field, Section } from './shared';

const DELIVERY_STATUS = {
  success: { label: 'Entregue', color: 'bg-green-100 text-green-700' },
  pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700' },
  failed: { label: 'Falhou', color: 'bg-red-100 text-red-600' },
};
const TARGETS = { crm: 'CRM', own: 'Webhook próprio' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

// The generated secret is shown once; it lives only in this component's state (never logged).
function GeneratedSecret({ secret, onDismiss }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(secret); toast.success('Segredo copiado'); } catch { toast.error('Não foi possível copiar'); }
  };
  return (
    <div className="rounded-xl border border-yellow-300 bg-yellow-50 p-4 space-y-2">
      <p className="font-bold text-sm">Segredo de assinatura do webhook</p>
      <p className="text-xs text-yellow-800">Copie agora e guarde em local seguro. Por segurança, ele não será exibido novamente.</p>
      <div className="flex gap-2">
        <input readOnly className="input font-mono text-xs" value={secret} onFocus={(e) => e.target.select()} />
        <button type="button" className="btn btn-primary" onClick={copy}>Copiar</button>
      </div>
      <button type="button" className="text-xs font-bold text-yellow-900 underline" onClick={onDismiss}>Já copiei, ocultar</button>
    </div>
  );
}

function Deliveries({ formId }) {
  const { data, isLoading, refetch, isFetching } = useQuery(['crm-form-deliveries', formId], () => crmFormsApi.getDeliveries(formId));
  const rows = data?.data?.deliveries || [];
  return (
    <Section title="Entregas recentes">
      <div className="flex justify-end -mt-10">
        <button type="button" className="text-sm text-primary font-bold" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Atualizando...' : 'Atualizar'}
        </button>
      </div>
      {isLoading ? <p className="text-sm text-slate-500">Carregando...</p> : rows.length === 0 ? (
        <p className="text-sm text-slate-500">Nenhuma entrega registrada.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <th className="py-2 pr-3">Destino</th><th className="pr-3">Evento</th><th className="pr-3">Status</th>
                <th className="pr-3">Tentativas</th><th className="pr-3">Data</th><th>Erro</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const st = DELIVERY_STATUS[d.status] || { label: d.status, color: 'bg-slate-100 text-slate-600' };
                return (
                  <tr key={d.id} className="border-t border-slate-100 align-top">
                    <td className="py-2 pr-3">{TARGETS[d.target] || d.target}</td>
                    <td className="pr-3">{d.eventType}</td>
                    <td className="pr-3"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span></td>
                    <td className="pr-3">{d.attempts}</td>
                    <td className="pr-3 whitespace-nowrap">{fmt(d.deliveredAt || d.createdAt)}</td>
                    <td className="text-xs text-red-600 break-words max-w-[240px]">{d.lastError || ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

export default function IntegrationTab({ form, onSaved, dirty }) {
  const { data: accData } = useQuery('crm-forms-accounts', () => crmFormsApi.getAccounts(), { staleTime: 5 * 60 * 1000 });
  const accounts = accData?.data?.accounts || [];

  const [accountId, setAccountId] = useState(form.chatwootAccountId ?? '');
  const [webhookUrl, setWebhookUrl] = useState(form.webhookUrl || '');
  const [secret, setSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [generated, setGenerated] = useState(null);
  const [urlError, setUrlError] = useState(null);

  const hasUrl = !!webhookUrl.trim();
  const secretInvalid = secret !== '' && secret.length < 16;
  const urlChanged = webhookUrl.trim() !== (form.webhookUrl || '');
  const accountChanged = String(accountId) !== String(form.chatwootAccountId ?? '');
  const changed = urlChanged || accountChanged || secret !== '';

  const save = async () => {
    setSaving(true);
    setUrlError(null);
    try {
      const body = { chatwootAccountId: accountId === '' ? null : Number(accountId) };
      if (urlChanged || secret) body.webhookUrl = webhookUrl.trim() || '';
      if (secret && hasUrl) body.webhookSecret = secret;
      const res = await crmFormsApi.update(form.id, body);
      const saved = res.data.form;
      if (saved.generatedWebhookSecret) setGenerated(saved.generatedWebhookSecret);
      setSecret('');
      setTestResult(null);
      onSaved(saved);
      toast.success('Integração salva');
    } catch (err) {
      // Show field-level errors next to the field, without the raw API field names.
      const msg = String(err.message || 'Erro ao salvar integração')
        .replace(/webhookUrl/g, 'A URL do webhook')
        .replace(/webhookSecret/g, 'O segredo');
      if (/url/i.test(msg)) setUrlError(msg);
      else toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await crmFormsApi.testWebhook(form.id);
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ ok: false, error: err.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-4">
      {generated && <GeneratedSecret secret={generated} onDismiss={() => setGenerated(null)} />}

      <Section title="Conta do CRM">
        <Field label="Vincular a uma conta do CRM (opcional)" hint="Vinculado, o lead é entregue ao CRM e as abas de mapeamento e roteamento ficam disponíveis. Sem vínculo, o formulário só coleta respostas.">
          <select className="input" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">Sem vínculo</option>
            {accounts.map((a) => <option key={a.chatwootAccountId} value={a.chatwootAccountId}>{a.name}</option>)}
          </select>
        </Field>
      </Section>

      <Section title="Webhook próprio">
        <Field label="URL do webhook (HTTPS)" hint="Receberá cada resposta (e abandono) assinada com HMAC-SHA256. Deixe vazio para remover.">
          <input
            className={`input ${urlError ? 'ring-2 ring-red-400' : ''}`} type="url" placeholder="https://exemplo.com/webhook"
            value={webhookUrl} aria-invalid={!!urlError}
            onChange={(e) => { setWebhookUrl(e.target.value); setUrlError(null); }}
          />
          {urlError && <p className="text-xs text-red-600 mt-1" role="alert">{urlError}</p>}
        </Field>
        <Field
          label={form.hasWebhookSecret ? 'Novo segredo (opcional)' : 'Segredo (opcional)'}
          hint={
            !hasUrl ? 'Informe a URL do webhook para configurar o segredo.'
              : form.hasWebhookSecret ? 'Já existe um segredo configurado. Preencha só para substituí-lo (mín. 16 caracteres).'
                : 'Vazio: geramos um segredo e mostramos uma única vez. Se informar, mín. 16 caracteres.'
          }
        >
          <input className="input font-mono disabled:opacity-50" type="password" autoComplete="new-password" disabled={!hasUrl} value={secret} onChange={(e) => setSecret(e.target.value)} />
          {secretInvalid && <p className="text-xs text-red-600 mt-1">O segredo deve ter ao menos 16 caracteres.</p>}
        </Field>
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className="btn btn-primary" disabled={!changed || saving || secretInvalid} onClick={save}>
            {saving ? 'Salvando...' : 'Salvar integração'}
          </button>
          <button type="button" className="btn btn-secondary" disabled={!form.webhookUrl || testing || urlChanged}
            title={urlChanged ? 'Salve a integração antes de testar' : undefined} onClick={test}>
            {testing ? 'Testando...' : 'Testar webhook'}
          </button>
          {dirty && <span className="text-xs text-slate-500">Alterações de perguntas/design são salvas pelo botão "Salvar" do topo.</span>}
        </div>
        {testResult && (
          <p className={`text-sm font-bold ${testResult.ok ? 'text-green-700' : 'text-red-600'}`}>
            {testResult.ok ? `Sucesso (HTTP ${testResult.status})` : `Falhou${testResult.status ? ` (HTTP ${testResult.status})` : ''}: ${testResult.error || 'erro desconhecido'}`}
          </p>
        )}
      </Section>

      <Deliveries formId={form.id} />
    </div>
  );
}
